import cn from 'classnames';
import debounce from 'lodash/debounce';
import ProfileNav from 'organisms/Navigation/ProfileNav';
import SignInNav from 'organisms/Navigation/SignInNav';
import SignUpNav from 'organisms/Navigation/SignUpNav';
import NotificationDialog from 'organisms/Notification/NotificationDialog/NotificationDialog';
import NotificationWrapper from 'organisms/Notification/NotificationWrapper';
import React, { useEffect, useMemo } from 'react';
import { useStore } from 'stores/store';
import { useUserAuth } from 'utils/hooks/useUserAuth';

import s from './Navigation.module.scss';

interface IAuthNav {
  transparent: boolean;
  dark: boolean;
  isSticky?: boolean;
  onAction: (actionName: string, url?: string) => void;
}

const AuthNav = ({ transparent, dark, isSticky, onAction }: IAuthNav) => {
  const store = useStore();
  const authDetails = useUserAuth();

  const fetchNotifications = useMemo(
    () =>
      debounce(() => {
        store.NotificationStore.listDirectNotifications({ filter: 'unread' });
      }, 1000),
    [store.NotificationStore]
  );

  useEffect(() => {
    if (authDetails?.isApproved) {
      fetchNotifications();
    }
  }, [authDetails.isApproved, fetchNotifications]);

  const getLink = (key: string, name: string, url: string) => {
    if (url == 'open_sign_in') {
      return (
        <SignInNav
          key={key}
          name={name}
          transparent={transparent}
          dark={dark}
          isSticky={isSticky}
        />
      );
    }

    if (url == 'open_sign_up') {
      return (
        <SignUpNav
          key={key}
          name={name}
          transparent={transparent}
          dark={dark}
          isSticky={isSticky}
        />
      );
    }

    if (url == 'open_profile') {
      return (
        <ProfileNav
          key={key}
          name={name}
          transparent={transparent}
          dark={dark}
          isSticky={isSticky}
          onAction={onAction}
        />
      );
    }
  };

  return (
    <div className={cn(s.Navigation__authWrapper, { transparent, dark, isSticky })}>
      <nav
        className={cn(s.Navigation__auth, { transparent, dark, isSticky })}
        data-testid="navigation_auth"
      >
        <div className={s.Navigation__profile}>
          {authDetails.isApproved && (
            <NotificationWrapper className={s.Navigation__notificationWrapper} />
          )}
          {authDetails.isApproved && (
            <NotificationDialog className={s.Navigation__notificationDialog} />
          )}
          {authDetails.isAuthenticated && getLink('nav-profile', 'Profile', 'open_profile')}
        </div>
        {!authDetails.isAuthenticated && !authDetails.isApproved && (
          <>
            {getLink('nav-signin', 'Log In', 'open_sign_in')}
            {getLink('nav-signup', 'Sign Up', 'open_sign_up')}
          </>
        )}
      </nav>
    </div>
  );
};

export default AuthNav;
